import React, { useMemo, useState } from 'react'
import { Action, FilterType, TreeFilterType } from './types'
import { ILeaf, INode } from 'shared/types/tree'
import { getFilteredResults } from './getFilteredResults'
import { Table, TableProps } from 'shared/ui/Table/Table'
import { createIdMap, NodeIdMap } from '../../version_mapping/utility'
import {
  OpenNodeButton,
  OpenNodeTKatBuilderButton,
} from '../../tree_navigation/TreeNavigation'
import { isNode } from 'shared/utils'
import { Box, Button } from '@mui/material'
import { ConfirmDialog } from 'shared/ui'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { doTheAction } from './doTheAction'
import { Column } from 'shared/ui/Table/types'
import { NodePath, nodePathString } from '../utility/NodePath'
import { ActionPreview } from './treeActions/ActionPreview'
import { getNodeUrl, getTKatBuilderNodeUrl } from '../../utility/TreeNavigation'

interface FilteredTreeTableProps
  extends Omit<TableProps<(INode | ILeaf)[]>, 'columns' | 'items'> {
  title?: string
  filter: TreeFilterType
  treeId: string
  versionId: string
  action?: Action
  customColumns?: Column<INode | ILeaf>[]
}

export function FilteredTreeTable({
  title,
  filter,
  treeId,
  versionId,
  action,
  customColumns = [],
}: FilteredTreeTableProps) {
  const { tree, resetTree } = useGetTreeById({ treeId, versionId })

  const idMap = useMemo(() => {
    return tree && createIdMap(tree)
  }, [tree])

  if (!tree || !idMap) return null

  const filteredItems = getFilteredResults(filter, tree)
  const tableTitle =
    title ??
    `${filteredItems.length} ${filter.filterType === FilterType.node ? 'Nodes' : 'Leaves'}`

  const cols = tableColumns({
    filter,
    onActionComplete: resetTree,
    idMap,
    action,
    customColumns,
  })
  return (
    <Table<INode | ILeaf>
      title={tableTitle}
      columns={cols}
      items={filteredItems}
      canExport={true}
      toolBarItems={[
        <ActionButton
          key="action"
          action={action}
          items={filteredItems}
          onActionComplete={resetTree}
          idMap={idMap}
        />,
      ]}
    />
  )
}

function ActionButton({
  action,
  items,
  onActionComplete,
  idMap,
}: {
  action?: Action
  onActionComplete?: () => void
  items: (ILeaf | INode)[]
  idMap: NodeIdMap
}) {
  const [confirming, setConfirming] = useState(false)

  if (!action || !items.length) return null

  const itemType = isNode(items[0]) ? 'Node' : 'Leaf'

  const onConfirmed = async () => {
    await doTheAction(action, items, idMap)
    setConfirming(false)
    onActionComplete?.()
  }

  return (
    <>
      <Button onClick={() => setConfirming(true)}>
        {action.actionType} {items.length} {itemType}
      </Button>
      <ConfirmDialog
        open={confirming}
        title={`${action.actionType} ${items.length} items`}
        onClose={() => setConfirming(false)}
        onConfirm={onConfirmed}
      >
        <ActionPreview action={action} />
      </ConfirmDialog>
    </>
  )
}

function tableColumns({
  filter,
  idMap,
  action,
  onActionComplete,
  customColumns,
}: {
  filter: TreeFilterType
  onActionComplete: undefined | (() => void)
  idMap: NodeIdMap
  action?: Action
  customColumns: Column<INode | ILeaf>[]
}) {
  const typeColumn = {
    title: 'Type',
    rowContent(item: INode | ILeaf) {
      return <Box>{item.type}</Box>
    },
    rowString(item: INode | ILeaf) {
      return item.type
    },
  }
  const titleColumn = {
    title: 'Title',
    rowContent(item: INode) {
      return <Box>{item.title}</Box>
    },
    rowString(item: INode) {
      return item.title
    },
  }
  const builderLinkColumn = {
    title: 'Open Builder',
    rowContent(item: INode | ILeaf) {
      return <OpenNodeTKatBuilderButton node={item} />
    },
    rowString(item: INode | ILeaf) {
      return getTKatBuilderNodeUrl(item)
    },
  }
  const pathColumn = {
    title: 'Node Path',
    rowContent(item: INode | ILeaf) {
      if (isNode(item)) {
        return <NodePath node={item} idMap={idMap} />
      }
      return null
    },
    rowString(item: INode | ILeaf) {
      if (isNode(item)) {
        return nodePathString({ node: item, idMap })
      }
      return ''
    },
  }
  const actionColumn = action
    ? [
        {
          title: action.actionType,
          rowContent(item: INode | ILeaf) {
            return (
              <ActionButton
                action={action}
                items={[item]}
                onActionComplete={onActionComplete}
                idMap={idMap}
              />
            )
          },
          skipExport: true,
        },
      ]
    : []
  switch (filter.filterType) {
    case FilterType.leaf:
      return [typeColumn, builderLinkColumn, ...actionColumn, ...customColumns]
    case FilterType.node:
      return [
        typeColumn,
        titleColumn,
        pathColumn,
        {
          title: 'Open Kiddom',
          rowContent(item: INode) {
            return <OpenNodeButton node={item} idMap={idMap} />
          },
          rowString(item: INode | ILeaf): string {
            return getNodeUrl(item, idMap)
          },
        },
        builderLinkColumn,
        ...actionColumn,
        ...customColumns,
      ]
    default:
      return []
  }
}
